import classNames from 'classnames'
import { motion } from 'framer-motion'
import React from 'react'
import { FieldError, UseFormRegister } from 'react-hook-form'

import { motionFadeIn } from '@apps-common/config/motion-variants'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  registration: Partial<UseFormRegister<any>>
  containerClassName?: string
  label: string
  description?: string
  errors?: FieldError
  info?: string
  // size?: 'sm' | 'lg'
  loading?: boolean
  disabled?: boolean
}

export const CheckboxInputSM = React.forwardRef<HTMLInputElement, React.PropsWithChildren<InputProps>>((props: InputProps, ref) => {
  const { className, containerClassName, registration, id, label, description, errors, info, loading = false, disabled = false } = props
  const checkBoxClasses = useProjectHook(
    'checked:[--chkfg:--rn-surface-2] checked:[--chkbg:--rn-brand] checked:bg-text-muted !bg-surface-2 text-text !border-brand',
    'checked:[--chkfg:--rn-ctx-surface-2] checked:[--chkbg:--rn-ctx-mm-brand] checked:bg-text-muted !bg-ctx-surface-2 text-text !border-ctx-mm-brand'
  )

  return (
    <>
      {loading && (
        <motion.div
          key={`${id}-checkbox-sm-skeleton`}
          {...motionFadeIn(0.2)}
          className={classNames('mb-2 animate-pulse', containerClassName)}
        >
          <div className='relative flex items-center justify-between pr-[10px] my-[0.5rem]'>
            <div className='bg-skeleton h-[20px] w-[60%] rounded-lg' />
            <div className='w-[22px] h-[22px] rounded bg-skeleton' />
          </div>
          <div className='h-[10px]' />
        </motion.div>
      )}
      {!loading && (
        <motion.div key={`${id}-checkbox-sm`} {...motionFadeIn(0.2)} className={classNames('mb-4', containerClassName)}>
          <div className='relative flex items-center justify-between my-[0.5rem]'>
            <div className='max-w-[80%]'>
              <label
                className={classNames('text-[0.9em] text-text-muted jost mr-[10px] pl-[10px]', disabled && 'text-text-muted opacity-60')}
                htmlFor={id}
              >
                {label}
              </label>
              {description && (
                <div className='mt-[5px] pl-2 text-[0.8rem] leading-[0.85rem] opacity-[0.55] text-text-muted/70'>{description}</div>
              )}
            </div>
            <input
              id={id}
              type='checkbox'
              className={classNames('checkbox', checkBoxClasses, className)}
              disabled={disabled}
              {...registration}
            />
          </div>
          <p
            className={classNames(
              'pl-2 pt-0 text-[12px]',
              info && !errors?.message && 'text-text-muted/70',
              (!info || errors?.message) && 'text-error'
            )}
          >
            {errors?.message || info}
          </p>
        </motion.div>
      )}
    </>
  )
})

CheckboxInputSM.displayName = 'Input'
